import { FcShape, fcAutoScaleLogo, fcAutoScaleText, type FcGeneratorDownloadType, type FcGeneratorOptions, type FcGeneratorPositionElementOptions, type FcPatternConfig, type FcText } from "@dtx-company/flowcode-generator-v2";
import { get } from "svelte/store";
import { FileFormat, type Code, type CodeDesign } from "#gen/codes/v3/codes_pb.js";
import type { AssetLoad } from "#lib/api/bundles.js";
import type { EntryAsset } from "#lib/assetManager.js";
import { downloadBlob } from "#lib/blob.js";
import { deepClone } from "#lib/deepClone.js";
import { convertPDFOrEPSImage, getImageDimensions, requireImageConversion } from "#lib/image.js";
import { Logger } from "#lib/logging.js";
import { numberFromStringValue } from "#lib/number.js";
import { ERR_INVALID_NUMBER } from "#lib/validation.js";
import { PUBLIC_GENERATOR_URL } from "$app/env/public";
import type { SelectOptionType } from "flowbite-svelte";

export const MAX_CENTER_TEXT_LENGTH = 15;
export const GENERATOR_DEFAULT_FONT = "inter_bold";
export const GENERATOR_FONTS: SelectOptionType<string>[] = [
	{ name: "Inter Black", value: "inter_black" },
	{ name: "Inter Bold", value: "inter_bold" },
	{ name: "Inter Extra Bold", value: "inter_extrabold" },
	{ name: "Inter Extra Light", value: "inter_extralight" },
	{ name: "Inter Light", value: "inter_light" },
	{ name: "Inter Medium", value: "inter_medium" },
	{ name: "Inter", value: "inter_regular" },
	{ name: "Inter Semi Bold", value: "inter_semibold" },
	{ name: "Inter Thin", value: "inter_thin" },
];

export const FcColorBlack = "#000000";
export const FcColorWhite = "#ffffff";

const NEW_EYE = "https://cdn.flowcode.com/prodassets/new_eye_PocmPIy.svg?ts=1702918245";
const NEW_FLOWCODE_EYE = "https://cdn.flowcode.com/prodassets/new_flowcode_eye.svg?ts=1702918886";

function fileReaderDownload<T = FcGeneratorDownloadType>(filename: string, targetFileType: T, blob: Blob): Promise<void> {
	return new Promise((resolve, reject): void => {
		const reader = new FileReader();
		reader.onload = function () {
			if (typeof reader.result === "string") {
				downloadBlob(`${filename}.${targetFileType}`, reader.result);
				resolve();
			}
			reject();
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

const CMYK_CAPABLE_FORMATS: ReadonlySet<string> = new Set(["eps", "tiff", "jpg", "pdf"]);

export function downloadUrl(fcConfig: FcGeneratorOptions, targetFileType: FcGeneratorDownloadType): string {
	const encodedOptions = encodeURIComponent(JSON.stringify(fcConfig));
	// Pass colorspace=cmyk when the config was authored as CMYK and the chosen format can carry a CMYK payload.
	const useCmyk = fcConfig.cmykPicked === true && CMYK_CAPABLE_FORMATS.has(targetFileType);
	const colorspace = useCmyk ? "&colorspace=cmyk" : "";
	const url = `${PUBLIC_GENERATOR_URL}/v1/flowcode?opts=${encodedOptions}&density=300&imageType=${targetFileType}${colorspace}`;
	return url;
}

export async function downloadFlowcodeBlob({ targetFileType, fcConfig }: { targetFileType: FcGeneratorDownloadType; fcConfig: FcGeneratorOptions }): Promise<Blob> {
	const url = downloadUrl(fcConfig, targetFileType);
	return fetch(url, {
		method: "GET",
		mode: "cors",
		headers: {
			"Content-Type": "application/json",
		},
	}).then(response => response.blob());
}

export async function downloadFlowcode({ targetFileType, filename, fcConfig }: { filename: string; targetFileType: FcGeneratorDownloadType; fcConfig: FcGeneratorOptions }): Promise<void> {
	return downloadFlowcodeBlob({ targetFileType, fcConfig }).then(blob => {
		fileReaderDownload(filename || "flowcode", targetFileType, blob).catch(err => Logger.error("Failed to download Flowcode", { err, filename, targetFileType }));
	});
}

export const CODE_PATTERN_CONFIGS: Omit<FcPatternConfig, "containerImageUrl">[] = [
	{
		id: "U3R1ZGlvQ29uZmlnT2JqZWN0VHlwZTpiZDhhOTk2OC0wZTE2LTQ5YTktYWEyMC05M2MzNTkxMmIzMDY=",
		name: "Generator 210630 / Circle",
		configuration: {
			gridModuleColorFillSvg: false,
			gridModuleRandomSizes: "75",
			containerOuterPatternColorFillSvg: false,
			containerOuterPatternRandomSizes: "75",
			containerOuterPatternShape: 1,
			gridModuleShape: 1,
			gridModuleImageUrl: null,
			containerOuterPatternImageUrl: null,
		},
		assetFile: {
			assetName: "Circle",
			url: "https://cdn.flowcode.com/prodassets/circle_XvDvMlQ.png?ts=1626201257",
		},
	},
	{
		id: "U3R1ZGlvQ29uZmlnT2JqZWN0VHlwZTo1YjY0ZDQzNS0wYzI0LTRmYjUtYTZhZS02YjQ0M2M0ZjkzOWY=",
		name: "Generator 210630 / Shaggy",
		configuration: {
			gridModuleColorFillSvg: false,
			gridModuleRandomSizes: "100",
			containerOuterPatternColorFillSvg: false,
			containerOuterPatternRandomSizes: "100",
			containerOuterPatternShape: 4,
			gridModuleShape: 4,
			gridModuleImageUrl: null,
			containerOuterPatternImageUrl: null,
		},
		assetFile: {
			assetName: "Standard",
			url: "https://cdn.flowcode.com/prodassets/standard_Kg9kdbA.png?ts=1626201244",
		},
	},
	{
		id: "U3R1ZGlvQ29uZmlnT2JqZWN0VHlwZTpmZWMwNGMwYi00ZjcyLTQ0YjctOTAwNC03MTZhZjA0ZjU3MWQ=",
		name: "Generator 210630 / Diamond",
		configuration: {
			gridModuleColorFillSvg: false,
			gridModuleRandomSizes: "105",
			containerOuterPatternColorFillSvg: false,
			containerOuterPatternRandomSizes: "105",
			containerOuterPatternShape: 3,
			gridModuleShape: 3,
			gridModuleImageUrl: null,
			containerOuterPatternImageUrl: null,
		},
		assetFile: {
			assetName: "Diamond",
			url: "https://cdn.flowcode.com/prodassets/diamond_MvAzlBf.png?ts=1626201262",
		},
	},
	{
		id: "U3R1ZGlvQ29uZmlnT2JqZWN0VHlwZTphMDQ4OTE1OS03YmM3LTRjMzAtYmJhZC1hMzU0ZTRlMDQ4ODU=",
		name: "Generator 210630 / Square",
		configuration: {
			gridModuleColorFillSvg: false,
			gridModuleRandomSizes: "75",
			containerOuterPatternColorFillSvg: false,
			containerOuterPatternRandomSizes: "75",
			containerOuterPatternShape: 2,
			gridModuleShape: 2,
			gridModuleImageUrl: null,
			containerOuterPatternImageUrl: null,
		},
		assetFile: {
			assetName: "Square",
			url: "https://cdn.flowcode.com/prodassets/square_sTQJtcg.png?ts=1626201237",
		},
	},
	{
		id: "U3R1ZGlvQ29uZmlnT2JqZWN0VHlwZTplYjAwMzA3Yi05YWU5LTQ4OGUtOWY1OS04NjRkYmJiNDkyYTk=",
		name: "Generator 210630 / Triangle",
		configuration: {
			gridModuleColorFillSvg: true,
			gridModuleRandomSizes: "110",
			containerOuterPatternColorFillSvg: true,
			containerOuterPatternRandomSizes: "110",
			containerOuterPatternShape: null,
			gridModuleShape: null,
			gridModuleImageUrl: "https://cdn.flowcode.com/prodassets/Asset_13_6IrDInz.svg?ts=1624978566926416304",
			containerOuterPatternImageUrl: "https://cdn.flowcode.com/prodassets/Asset_13_6IrDInz.svg?ts=1624978566926416304",
		},
		assetFile: {
			assetName: "Triangle",
			url: "https://cdn.flowcode.com/prodassets/triangle_NopwLmQ.png?ts=1626201252",
		},
	},
	{
		id: "U3R1ZGlvQ29uZmlnT2JqZWN0VHlwZTplMDRlZjYxNS1kZjZjLTQwYjEtOGU0Zi03OGQ0ZTM0MzJmMjY=",
		name: "Generator 210630 / Star",
		configuration: {
			gridModuleColorFillSvg: true,
			gridModuleRandomSizes: "115",
			containerOuterPatternColorFillSvg: true,
			containerOuterPatternRandomSizes: "115",
			containerOuterPatternShape: null,
			gridModuleShape: null,
			gridModuleImageUrl: "https://cdn.flowcode.com/prodassets/Star_1_paabDgX.svg?ts=1624978619911315131",
			containerOuterPatternImageUrl: "https://cdn.flowcode.com/prodassets/Star_1_paabDgX.svg?ts=1624978620875933822",
		},
		assetFile: {
			assetName: "Star",
			url: "https://cdn.flowcode.com/prodassets/star_rbwTKBR.png?ts=1626201274",
		},
	},
	{
		id: "U3R1ZGlvQ29uZmlnT2JqZWN0VHlwZTo2NTBiMDdiNy05YTcxLTQ3N2ItOTM4MC05YmNjZjNlY2ZkNjI=",
		name: "Generator 210630 / Heart",
		configuration: {
			gridModuleColorFillSvg: true,
			gridModuleRandomSizes: "95",
			containerOuterPatternColorFillSvg: true,
			containerOuterPatternRandomSizes: "95",
			containerOuterPatternShape: null,
			gridModuleShape: null,
			gridModuleImageUrl: "https://cdn.flowcode.com/prodassets/Asset_3_uth1BOa.svg?ts=1647637299483460658",
			containerOuterPatternImageUrl: "https://cdn.flowcode.com/prodassets/Asset_3_uth1BOa.svg?ts=1647637299483460658",
		},
		assetFile: {
			assetName: "Heart",
			url: "https://cdn.flowcode.com/prodassets/heart_goCD11J.png?ts=1626201269",
		},
	},
];

const DEFAULT_POSITIONS: Required<Pick<FcGeneratorOptions, "positionElementBottomLeft" | "positionElementTopLeft" | "positionElementTopRight">> = {
	positionElementBottomLeft: {
		backgroundColor: FcColorWhite,
		fillSvg: false,
		innerImageUrl: null,
		innerShape: 2,
		innerShapeColor: FcColorBlack,
		innerShapeWidth: 45,
		outerShape: 5,
		outerShapeColor: FcColorBlack,
		outerShapeWidth: 15,
		text: {
			font: "inter_black",
			fontColor: FcColorBlack,
			fontSize: 12,
			position: {
				offsetPercentage: 0,
				property: 1,
				extraDegrees: 0,
			},
			text: "FLOWCODE",
		},
	},
	positionElementTopLeft: {
		backgroundColor: FcColorWhite,
		fillSvg: false,
		innerImageUrl: null,
		innerShape: 2,
		innerShapeColor: FcColorBlack,
		innerShapeWidth: 45,
		outerShape: 2,
		outerShapeColor: FcColorBlack,
		outerShapeWidth: 15,
	},
	positionElementTopRight: {
		backgroundColor: FcColorWhite,
		fillSvg: false,
		innerImageUrl: null,
		innerShape: 2,
		innerShapeColor: FcColorBlack,
		innerShapeWidth: 45,
		outerShape: 2,
		outerShapeColor: FcColorBlack,
		outerShapeWidth: 15,
	},
};

function createText(text: string, color: string, font: string, position: number): FcText {
	return {
		font: font,
		fontSize: 20,
		text,
		position: {
			property: position,
			offsetPercentage: 0,
			extraDegrees: 0,
		},
		fontColor: color,
	};
}

export function findPatternId(config: FcGeneratorOptions): string {
	function matches(pattern: Omit<FcPatternConfig, "containerImageUrl">): boolean {
		if (pattern.configuration.gridModuleShape && pattern.configuration.gridModuleShape === config?.gridModuleShape) {
			return true;
		}
		if (pattern.configuration.gridModuleImageUrl && pattern.configuration.gridModuleImageUrl === config?.gridModuleImageUrl) {
			return true;
		}
		return false;
	}
	return CODE_PATTERN_CONFIGS.find(matches)?.id ?? CODE_PATTERN_CONFIGS[0]?.id;
}

export async function setCenterText(config: FcGeneratorOptions, text?: string): Promise<FcGeneratorOptions> {
	const copy = { ...config };

	if (!text) {
		delete copy.centerText;
		copy.centerTextPadding = 0;
	} else {
		// Remove any existing logo attributes
		if (copy.logoImageUrl) {
			delete copy.logoImageUrl;
			copy.logoHeight = 0;
			copy.logoWidth = 0;
			copy.logoOuterPadding = 0;
		}

		copy.centerTextPadding = 4;
		copy.centerText = [
			{
				text: text ?? "",
				font: "inter_bold",
				fontColor: config.gridModuleColor || FcColorBlack,
				position: {
					property: 0,
					offsetPercentage: 0,
					extraDegrees: 0,
				},
				fontSize: 12,
			},
		];
	}

	// If the text is a data attribute, autoscale with a fixed width to prevent
	// the generator from trying to break the data attribute into multiple lines.
	// The data attribute will be filled in at a later point.
	let fixedTextWidth = 0;
	if (text && text.includes("{{") && text.includes("}}")) {
		fixedTextWidth = MAX_CENTER_TEXT_LENGTH;
	}

	const updatedConfg = await fcAutoScaleText(copy, 12, 24, true, false, true, fixedTextWidth);

	// The fcAutoScaleText adds some extra properties to the text objects that
	// we don't want and crashes when trying to save through Buff
	if (updatedConfg.centerText) {
		for (let i = 0; i < updatedConfg.centerText.length; i++) {
			delete updatedConfg.centerText[i].fontOutlineColor;
			delete updatedConfg.centerText[i].fontOutlineSize;
			delete updatedConfg.centerText[i].gradient;
			delete updatedConfg.centerText[i].rotation;
			delete updatedConfg.centerText[i].shape;
		}
	}

	// Defined value set by generator, but fails buff validation
	if (updatedConfg?.centerTextBlendErase === undefined) {
		delete updatedConfg.centerTextBlendErase;
	}

	return deepClone(updatedConfg);
}

export function getCenterText(config?: FcGeneratorOptions): FcText | undefined {
	if (config?.centerText === undefined) {
		return undefined;
	}

	// This isn't necessarily always the full value. We could have an array the makes up the center text
	return Array.isArray(config.centerText) && config.centerText.length > 0 ? config.centerText[0] : undefined;
}

export function getTopText(config: FcGeneratorOptions): FcText | undefined {
	return config.containerRimText?.find((textConfig: FcText) => textConfig?.position?.property === 6);
}

export function setTopText(config: FcGeneratorOptions, text?: string): FcGeneratorOptions {
	const current = getTopText(config);
	if (current === text) {
		return config;
	}

	const updated = setText(config, text ?? "", 6);
	const bottomText = getBottomText(updated);
	if ((text ?? "") === "" && (bottomText?.text ?? "") === "") {
		updated.containerRimWidth = 0;
		updated.containerRimBorderSize = 0;
	} else {
		updated.containerRimWidth = 15;
		updated.containerRimBorderSize = 2;
	}
	return updated;
}

export function setTopTextFont(config: FcGeneratorOptions, font: string): FcGeneratorOptions {
	return setTextFont(config, font, 6);
}

export function setBottomTextFont(config: FcGeneratorOptions, font: string): FcGeneratorOptions {
	return setTextFont(config, font, 7);
}

function setTextFont(config: FcGeneratorOptions, font: string, position: number): FcGeneratorOptions {
	const fcText = config.containerRimText?.find((textConfig: FcText) => textConfig?.position?.property === position);
	if (!fcText) {
		if (!config.containerRimText) {
			config.containerRimText = [];
		}
		config.containerRimText.push(createText("", FcColorBlack, font, position));
	} else {
		fcText.font = font;
	}
	return config;
}

export function setTopTextColor(config: FcGeneratorOptions, color?: string): FcGeneratorOptions {
	return setTextColor(config, color ?? FcColorBlack, 6);
}

export function getBottomText(config: FcGeneratorOptions): FcText | undefined {
	return config.containerRimText?.find((textConfig: FcText) => textConfig?.position?.property === 7);
}

export function setBottomText(config: FcGeneratorOptions, text?: string): FcGeneratorOptions {
	const current = getBottomText(config);
	if (current === text) {
		return config;
	}

	const updated = setText(config, text ?? "", 7);
	const topText = getTopText(updated);
	if ((text ?? "") === "" && (topText?.text ?? "") === "") {
		updated.containerRimWidth = 0;
		updated.containerRimBorderSize = 0;
	} else {
		updated.containerRimWidth = 15;
		updated.containerRimBorderSize = 2;
	}
	return updated;
}

export function setBottomTextColor(config: FcGeneratorOptions, color?: string): FcGeneratorOptions {
	return setTextColor(config, color ?? FcColorBlack, 7);
}

function setText(config: FcGeneratorOptions, text: string, position: number): FcGeneratorOptions {
	const fcText = config.containerRimText?.find((textConfig: FcText) => textConfig?.position?.property === position);
	if (!fcText) {
		if (!config.containerRimText) {
			config.containerRimText = [];
		}
		config.containerRimText.push(createText(text, FcColorBlack, GENERATOR_DEFAULT_FONT, position));
	} else {
		fcText.text = text;
	}
	return config;
}

function setTextColor(config: FcGeneratorOptions, color: string, position: number): FcGeneratorOptions {
	const fcText = config.containerRimText?.find((textConfig: FcText) => textConfig?.position?.property === position);
	if (!fcText) {
		if (!config.containerRimText) {
			config.containerRimText = [];
		}
		config.containerRimText.push(createText("", color, GENERATOR_DEFAULT_FONT, position));
	} else {
		fcText.fontColor = color;
	}
	config.alwaysUseDefaultColor = false;
	return config;
}

export function setRimBackgroundColor(config: FcGeneratorOptions, color?: string): FcGeneratorOptions {
	config.containerRimBackgroundColor = color;
	if (color) {
		delete config.defaultBackgroundColor;
	} else {
		config.defaultBackgroundColor = FcColorWhite;
	}
	return config;
}

export function setRimBorderColor(config: FcGeneratorOptions, color?: string): FcGeneratorOptions {
	config.containerRimBorderColor = color;
	return config;
}

export async function setShape(config: FcGeneratorOptions, shape: string, interpolatedImageUrl?: string): Promise<FcGeneratorOptions> {
	const color = config?.containerBorderColor ?? FcColorBlack;
	if (shape === "square") {
		const update: FcGeneratorOptions = {
			containerShape: 2,
			dataGridPercentageSize: 92,
			containerImageOverlayPrivacy: false,
			containerImageBackgroundOnly: false,
			containerImageUrlFillSvg: true,
			containerImageUrl: null,
			radiusMask: 89,
			containerBorderSize: 10,
			containerEmptySpaceSize: 10,
			containerOuterPatternColor: config.containerBorderColor,
			containerText: [
				{
					font: "inter_regular",
					fontColor: color,
					fontSize: 9,
					position: {
						offsetPercentage: 3.6,
						property: 7,
						extraDegrees: 0,
					},
					text: "PRIVACY.FLOWCODE.COM",
				},
			],
		};
		config = { ...config, ...update };
	} else if (shape === "circle") {
		const update: FcGeneratorOptions = {
			containerShape: 1,
			dataGridPercentageSize: 70,
			containerImageOverlayPrivacy: true,
			containerImageBackgroundOnly: true,
			containerImageUrlFillSvg: null,
			containerImageUrl: null,
			containerOuterPatternColor: config.gridModuleColor,
			containerText: [
				{
					font: "inter_medium",
					fontColor: color,
					fontSize: 9,
					position: {
						offsetPercentage: 3.1,
						property: 7,
						extraDegrees: 0,
					},
					text: "PRIVACY.FLOWCODE.COM",
				},
			],
			containerBorderSize: 8.5,
			containerEmptySpaceSize: 6,
		};
		config = { ...config, ...update };
	}

	if (config.logoImageUrl) {
		// If the shape changes, we should recalculate the logo size
		config = await setImage(config, config.logoImageUrl, interpolatedImageUrl);
	}

	return config;
}

export function setPattern(config: FcGeneratorOptions, patternId: string): FcGeneratorOptions {
	const patternConfig: Omit<FcPatternConfig, "containerImageUrl"> = CODE_PATTERN_CONFIGS.find(({ id }) => id === patternId) || CODE_PATTERN_CONFIGS[0];
	config = { ...config, ...patternConfig.configuration };

	if (config.containerShape !== FcShape.SQUARE) {
		config.containerOuterPatternColorFillSvg = true;
	}

	return config;
}

// Resize center image based on some max dimension, this can be renamed if it has a more generic use case
async function getLogoDimensions(src: File | string | null, maxDimensionSize = 30): Promise<{ height: number; width: number }> {
	// Browsers can't load EPS/PDF into an <img>, so measure the converted
	// SVG instead, which carries the original viewBox aspect ratio.
	let dimensionSrc = src;
	if (typeof src === "string" && requireImageConversion(src, "")) {
		const converted = await convertPDFOrEPSImage(src, "");
		if (converted) {
			dimensionSrc = converted;
		}
	}
	const { width, height } = await getImageDimensions(dimensionSrc);
	const max = Math.max(width, height);
	const min = Math.min(width, height);
	const proportionalDimension = (maxDimensionSize * min) / max;
	const isWidthLargest = max === width;
	return {
		height: isWidthLargest ? proportionalDimension : maxDimensionSize,
		width: isWidthLargest ? maxDimensionSize : proportionalDimension,
	};
}

interface SetImageOptions {
	skipCropping?: boolean;
}

function swapPositionCorners(config: FcGeneratorOptions): void {
	const bottomLeft = config.positionElementBottomLeft;
	const topRight = config.positionElementTopRight;

	config.positionElementBottomLeft = { ...topRight };
	config.positionElementTopRight = { ...bottomLeft };
}

export async function setImage(config: FcGeneratorOptions, image: string | undefined | null, interpolatedImage?: string, options?: SetImageOptions): Promise<FcGeneratorOptions> {
	// Set the default (no logo)
	config.logoImageUrl = "";
	config.logoHeight = 0;
	config.logoWidth = 0;
	config.logoOuterPadding = 2;

	if (typeof image === "string" && image.length > 0) {
		// Make sure we don't keep any center text if a logo is set
		config.centerText = [];
		config.centerTextPadding = 0;

		const logoImageUrl = interpolatedImage?.length ? interpolatedImage : image;

		try {
			if (options?.skipCropping !== true) {
				// Try to autoscale the logo
				const { options: { logoHeight: autoScaledHeight, logoWidth: autoScaledWidth, qrrotate } = {} } = await fcAutoScaleLogo({ ...config, logoImageUrl }, undefined, true, false);

				if (typeof qrrotate === "boolean" && qrrotate !== (config.qrrotate ?? false)) {
					swapPositionCorners(config);
					config.qrrotate = qrrotate;
				}

				config.logoHeight = autoScaledHeight || 0;
				config.logoWidth = autoScaledWidth || 0;
				config.logoOuterPadding = 2;
			}
		} catch (_) {
			// Do nothing
		} finally {
			config.logoImageUrl = logoImageUrl;

			if ((config.logoHeight === 0 || config.logoWidth === 0) && logoImageUrl) {
				try {
					const { width, height } = await getLogoDimensions(logoImageUrl);
					config.logoWidth = width;
					config.logoHeight = height;
				} catch (err) {
					Logger.error(`Unable to set logo dimensions for ${logoImageUrl}`, { err });
				}
			}
		}
	} else {
		if (config.qrrotate) {
			swapPositionCorners(config);
			config.qrrotate = false;
		}
	}
	return config;
}

export function setPrimaryColor(config: FcGeneratorOptions, color: string): FcGeneratorOptions {
	config.alwaysUseDefaultColor = false;
	config.containerBorderColor = color;

	// Primary color is used as the border color for square codes
	if (config.containerShape === FcShape.SQUARE) {
		config.containerOuterPatternColorFillSvg = true;
		config.containerOuterPatternColor = color;
	}

	if (config.containerText?.[0]) {
		config.containerText[0].fontColor = color;
	}
	return config;
}

export function setSecondaryColor(config: FcGeneratorOptions, color: string): FcGeneratorOptions {
	config.alwaysUseDefaultColor = false;

	// The secondary color is used as the border for square codes
	if (config.containerShape !== FcShape.SQUARE) {
		config.containerOuterPatternColor = color;
	}

	config.gridModuleColor = color;
	config.positionElementBottomLeft = setPositionColor(config.positionElementBottomLeft ?? deepClone(DEFAULT_POSITIONS["positionElementBottomLeft"]), color);
	config.positionElementTopLeft = setPositionColor(config.positionElementTopLeft ?? deepClone(DEFAULT_POSITIONS["positionElementTopLeft"]), color);
	config.positionElementTopRight = setPositionColor(config.positionElementTopRight ?? deepClone(DEFAULT_POSITIONS["positionElementTopRight"]), color);

	// Any center text should also be set to the secondary color
	if (config.centerText) {
		for (let i = 0; i < config.centerText.length; i++) {
			config.centerText[i].fontColor = color;
		}
	}

	return config;
}

export function validateSecondaryColor(config: FcGeneratorOptions, color: string): string {
	if ((config.containerBackgroundColor === color || config.containerBackgroundColor === undefined) && color === FcColorWhite) {
		return FcColorBlack;
	} else if ((config.containerBackgroundColor === color || config.containerBackgroundColor === undefined) && color === FcColorBlack) {
		return FcColorWhite;
	}
	return color;
}

function setPositionColor(pos: FcGeneratorPositionElementOptions, color: string): FcGeneratorPositionElementOptions {
	pos.outerShapeColor = color;
	pos.innerShapeColor = color;
	if (pos.text) {
		pos.text.fontColor = color;
	}
	return pos;
}

function positionHasFlowcodeBranding(pos: FcGeneratorPositionElementOptions | undefined): boolean {
	if (!pos) {
		return false;
	}
	if (pos.imageUrl) {
		return pos.imageUrl === NEW_FLOWCODE_EYE;
	}
	return pos.outerShape === FcShape.LOGO;
}

export function hasFlowcodeBranding(config: FcGeneratorOptions): boolean {
	return positionHasFlowcodeBranding(config?.positionElementBottomLeft) || positionHasFlowcodeBranding(config?.positionElementTopRight);
}

function setPositionFlowcodeBranding(pos: FcGeneratorPositionElementOptions, enabled: boolean): void {
	if (pos.imageUrl) {
		pos.imageUrl = enabled ? NEW_FLOWCODE_EYE : NEW_EYE;
	} else {
		pos.outerShape = enabled ? FcShape.LOGO : FcShape.SQUARE;
	}
}

export function setUseFlowcodeBranding(config: FcGeneratorOptions, enabled: boolean): FcGeneratorOptions {
	if (!config.positionElementBottomLeft) {
		config.positionElementBottomLeft = deepClone(DEFAULT_POSITIONS.positionElementBottomLeft);
	}
	if (!config.positionElementTopRight) {
		config.positionElementTopRight = deepClone(DEFAULT_POSITIONS.positionElementTopRight);
	}
	// Branding should only ever appear on the visually-bottom corner: positionElementTopRight
	// when the code is rotated, positionElementBottomLeft otherwise. The opposite corner is
	// always cleared so stale branding (e.g. from a rotation swap) can't leak through.
	const brandingCorner = config.qrrotate ? config.positionElementTopRight : config.positionElementBottomLeft;
	const otherCorner = config.qrrotate ? config.positionElementBottomLeft : config.positionElementTopRight;
	setPositionFlowcodeBranding(brandingCorner, enabled);
	setPositionFlowcodeBranding(otherCorner, false);
	return config;
}

export function getCodeConfig(asset?: EntryAsset): FcGeneratorOptions {
	if (!asset) {
		return {};
	}
	const code = asset as Code;
	return (code.codeDesign?.config as FcGeneratorOptions) ?? {};
}

export function getCodeDesign(asset?: AssetLoad): CodeDesign | undefined {
	const batch = asset?.asCodeBatch();
	if (batch) {
		return get(batch).baseDesign;
	}
	const code = asset?.asCode();
	if (code) {
		return get(code).codeDesign;
	}
	return undefined;
}

export function getFileFormatLabel(format: FileFormat): string {
	switch (format) {
		case FileFormat.PNG:
			return "PNG";
		case FileFormat.JPG:
			return "JPG";
		case FileFormat.SVG:
			return "SVG";
		case FileFormat.PDF:
			return "PDF";
		case FileFormat.EPS:
			return "EPS";
		case FileFormat.TIFF:
			return "TIFF";
		default:
			return "";
	}
}

const MASK_WIDTH_MAX_INCHES = 12;
const MASK_EXPORT_DENSITY = 300;

export const ERR_MASK_WIDTH_RANGE = `Enter a size between 0 and ${MASK_WIDTH_MAX_INCHES} inches.`;

type MaskWidthResult = { state: "empty" } | { state: "invalid"; message: string } | { state: "valid"; inches: number };

const INVISIBLE_CHARACTERS = /[\u200B-\u200D\uFEFF]/g;

export function parseMaskWidthInches(value: string): MaskWidthResult {
	const cleaned = value.replaceAll(INVISIBLE_CHARACTERS, "");
	if (cleaned.trim() === "") {
		return { state: "empty" };
	}
	const inches = numberFromStringValue(cleaned);
	if (inches === undefined) {
		return { state: "invalid", message: ERR_INVALID_NUMBER };
	}
	if (inches <= 0 || inches > MASK_WIDTH_MAX_INCHES) {
		return { state: "invalid", message: ERR_MASK_WIDTH_RANGE };
	}
	return { state: "valid", inches };
}

export function maskPixelWidth(inches: number): number {
	return Math.floor(inches * MASK_EXPORT_DENSITY);
}

export function isPixelDimensioned(format: FileFormat): boolean {
	switch (format) {
		case FileFormat.PNG:
		case FileFormat.JPG:
		case FileFormat.TIFF:
		case FileFormat.SVG:
			return true;
		default:
			return false;
	}
}
